import { useState } from 'react';
import { Button, Dialog } from '@awj/ui';

import { useI18n } from '~/i18n/index.tsx';

interface Request {
  title: string;
  body?: string;
  action: string;
  resolve: (confirmed: boolean) => void;
}

/**
 * A second click in front of something that cannot be undone.
 *
 * `await confirm.ask({ title, body })` resolves true when the action is confirmed and false when
 * it is cancelled; render `confirm.dialog` once in the component. Used where a whole page of its
 * own would be too much — a row's delete button, an image on a card.
 */
export function useConfirm() {
  const { t } = useI18n();
  const [request, setRequest] = useState<Request | null>(null);

  function close(confirmed: boolean) {
    request?.resolve(confirmed);
    setRequest(null);
  }

  const ask = (options: { title: string; body?: string; action?: string }) =>
    new Promise<boolean>((resolve) =>
      setRequest({ ...options, action: options.action ?? t('common.delete'), resolve }),
    );

  const dialog = (
    <Dialog
      open={request !== null}
      onClose={() => close(false)}
      title={request?.title ?? ''}
      closeLabel={t('common.close')}
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => close(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={() => close(true)}>
            {request?.action}
          </Button>
        </>
      }
    >
      {request?.body ? <p className="dialog-lede">{request.body}</p> : null}
    </Dialog>
  );

  return { ask, dialog };
}
